import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BooleanOperator, ServiceType } from '@common/enums';
import {
  AutoActivationMode,
  AzureGroup,
  BackupSetting,
  BackupSettingPayload,
  OdataObject,
  OrganizationalUnit,
  PageState,
  SelectedPolicies,
  TasksStateResponse,
  User,
  UsersPermissions,
  UsersServices
} from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { AbilityService } from '@common/services/auth/ability.service';
import { OrganizationalUnitsService } from '@common/services/organizational-units.service';
import { SharedOdataService } from '@common/services/shared.odata.service';
import { FilterHelper } from '@common/services/users/helpers/filter.helper';
import { UtilsHelper } from '@common/services/users/helpers/utils.helper';
import { getOdataTop } from '@common/utils';
import { containsWrapper } from '@common/utils/functions/search';
import { Permissions } from '@pages/users/user-sidepanel/user-sidepanel.model';
import { I18NextPipe } from 'angular-i18next';
import { naturalSort } from 'mbs-ui-kit';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export enum UsersFilter {
  All = 'All',
  InDomain = 'InDomain',
  NotInDomain = 'NotInDomain',
  Suspended = 'Suspended'
}

@Injectable({ providedIn: 'root' })
export class UserOdataService extends SharedOdataService<User> {
  private readonly ability = inject(AbilityService);
  private readonly http = inject(HttpClient);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly orgUnitsService = inject(OrganizationalUnitsService);

  private readonly allUsersText = this.i18nPipe.transform('common.availableSelect.allUsers', { format: 'capitalize' });
  private readonly backupUsers = this.odataFactory.CreateService<any>('BackupUsers');

  accessor providerStateFilters = [
    { value: UsersFilter.All, text: this.allUsersText },
    { value: UsersFilter.InDomain, text: this.i18nPipe.transform('common.availableSelect.usersInDomain', { format: 'capitalize' }) },
    {
      value: UsersFilter.NotInDomain,
      text: this.i18nPipe.transform('common.availableSelect.usersNotInDomain', { format: 'capitalize' })
    },
    { value: UsersFilter.Suspended, text: this.i18nPipe.transform('common.availableSelect.blockedSuspended', { format: 'capitalize' }) }
  ];

  protected readonly isGoogle = this.ability.isGoogle;
  protected readonly isOffice = this.ability.isOffice;

  constructor() {
    super('');
  }

  fetchUsers(state: PageState): Observable<ODataPagedResult<User>> {
    const { groupId, filter, policyType, searchTerm } = state;
    const isOffice = this.isOffice();
    const isGoogle = this.isGoogle();
    const filters: string[] = [];

    FilterHelper.applyProviderState(filters, state);
    FilterHelper.applySearch(filters, searchTerm);
    FilterHelper.applyServices(filters, { serviceFilters: filter, providerStateValue: this.providerStateFilters[2].value });
    FilterHelper.applyInBackup(filters, state);
    FilterHelper.applyPolicyType(filters, { policyType, isOffice, isGoogle });

    let odataService = this.backupUsers
      .Query()
      .OrderBy(this.orderBy)
      .Top(this.pageSize)
      .Skip(this.pageSize * (this.page - 1))
      .CustomQueryOptions(FilterHelper.applyIncludePolicyData(true));

    if (filters.length) {
      odataService = odataService.Filter(filters.join(` ${BooleanOperator.And} `));
    }

    if (groupId) {
      odataService = odataService.CustomQueryOptions([{ key: 'folderId', value: groupId }, FilterHelper.applyIncludePolicyData(true)]);
    }

    return odataService.ExecWithCount() as unknown as Observable<ODataPagedResult<User>>;
  }

  deleteUser(ids: string | string[]): Observable<any> {
    const deleteSelected$ = this.backupUsers.CustomCollectionAction('DeleteSelected', { Ids: ids });
    const delete$ = this.backupUsers.CustomAction(ids, 'Delete', null);

    return Array.isArray(ids) ? deleteSelected$ : delete$;
  }

  getPermissions(userIds: string[]): Observable<UsersPermissions> {
    const odataService = this.odataFactory.CreateService(`GetUsersPermissions?userIds=${userIds.join(',')}`);

    return this.http.get<UsersPermissions>(odataService.Query().GetUrl());
  }

  updatePermissions(userIds: string[], permissions: Permissions): Observable<any> {
    const { signInEnable: SignInEnable, restoreEnable: RestoreEnable } = permissions;
    const options = {
      Ids: userIds,
      SignInEnable,
      RestoreEnable
    };

    return this.odata.CustomCollectionAction('SetUsersPermissions', options);
  }

  getServices(userIds: string[]): Observable<UsersServices> {
    const odataService = this.odataFactory.CreateService(`GetUsersServices?userIds=${userIds.join(',')}`);

    return this.http.get<UsersServices>(odataService.Query().GetUrl());
  }

  configureServices(userIds: string[], backupSettings: BackupSetting[]): Observable<any> {
    const payload: BackupSettingPayload = {
      Ids: userIds,
      Services: backupSettings.map((s) => ({ Service: ServiceType[s.serviceType], Enabled: s.enabled }))
    };

    return this.odata.CustomCollectionAction('ConfigureServices', payload);
  }

  updateAttachedPolicies(userIds: string[], policy: SelectedPolicies): Observable<any> {
    const options = {
      UserIds: userIds,
      EmailPolicyId: UtilsHelper.convertPolicyId(policy.emailPolicyId),
      DrivePolicyId: UtilsHelper.convertPolicyId(policy.drivePolicyId),
      ContactPolicyId: UtilsHelper.convertPolicyId(policy.contactPolicyId),
      CalendarPolicyId: UtilsHelper.convertPolicyId(policy.calendarPolicyId),
      SitePolicyId: UtilsHelper.convertPolicyId(policy.sitePolicyId),
      TeamDrivePolicyId: UtilsHelper.convertPolicyId(policy.teamDriveId),
      TeamsPolicyId: UtilsHelper.convertPolicyId(policy.teamsPolicyId)
    };

    return this.odata.CustomCollectionAction('SetAttachedPolicies', options);
  }

  getTasksState(userId: string): Observable<TasksStateResponse> {
    return this.backupUsers.CustomFunction(userId, 'GetTasksState').pipe(map((res: HttpResponse<TasksStateResponse>) => res.body));
  }

  getUserGroups(search?: string): Observable<AzureGroup[]> {
    const odataService = this.odataFactory.CreateService(`AzureGroups`);
    const top = getOdataTop(search);
    const orderBy = search ? 'Name asc' : null;
    const filter = search ? containsWrapper('Name', search) : '';

    return this.http.get<OdataObject<AzureGroup[]>>(odataService.Query().Top(top).OrderBy(orderBy).Filter(filter).GetUrl()).pipe(
      map((res) => res.value),
      map((groups: AzureGroup[]) => {
        groups.toSorted((a, b) => naturalSort(a.Name, b.Name));
        groups.unshift({
          Id: '',
          Name: this.allUsersText,
          AutoActivationMode: AutoActivationMode.Disabled
        });

        return groups;
      })
    );
  }

  getOrganizationalUnitsGroups(search?: string): Observable<OrganizationalUnit[]> {
    this.orgUnitsService.orderBy = search ? 'Path asc' : null;
    this.orgUnitsService.pageSize = getOdataTop(search);
    this.orgUnitsService.filter = search ? containsWrapper('Path', search) : '';

    return this.orgUnitsService.fetchGroups().pipe(
      map((res) => res.data),
      map((units) => UtilsHelper.normalizeUnits(units, { allUsersText: this.allUsersText, search }))
    );
  }

  addAllToBackup(): Observable<any> {
    return this.odata.CustomCollectionAction('AddAllUsersToBackup', null);
  }

  setAutoAddUserToBackup(autoAddUserToBackup: boolean): Observable<any> {
    return this.odata.CustomCollectionAction(`SetAutoAddUserToBackup?autoAddUserToBackup=${String(autoAddUserToBackup)}`, null);
  }

  setAutoAddGroupToBackup(autoAddGroupToBackup: boolean): Observable<any> {
    return this.odata.CustomCollectionAction(`SetAutoAddGroupToBackup?autoAddGroupToBackup=${String(autoAddGroupToBackup)}`, null);
  }
}
